import { useState } from "react";
import { Link } from "react-router-dom";
import { websiteLink, toSlugUrl, initials } from "../../../helpers/helpers";
import paths from "../../../constants/paths";
import http from "../../../helpers/http";
import { doObjToFormData } from "../../../helpers/helpers";
import { getAuthUserToken } from "../../../helpers/authToken";
import ImageControl from "../../common/ImageControl";

/**
 * One card for every employer in the directory, however much we know.
 *
 * The page has two ways in - search a company, or search a profession - and
 * they return employers from two different places: our own profiled records,
 * and the Home Office release of Certificates of Sponsorship, which covers
 * 60,222 organisations we have never profiled. Rather than two lists or two
 * cards, this is one card with a stat line that renders the facts it has.
 *
 * So a profiled employer shows its rating, strength, overseas policy and live
 * jobs alongside its sponsorship volume; an employer we only know from the
 * FOI file shows volume, how many other roles it sponsors, and its Companies
 * House number. Neither reads as broken, because nothing renders as an empty
 * slot - a fact we do not hold is simply absent.
 *
 * Styling lives in _main.scss under .directory_card. Nothing here sets a font
 * size: Red Hat Display is loaded as separate single-weight families, so
 * weights belong in the stylesheet with the rest of the site's type.
 */

const RATINGS = {
  a_rated: "A-rated sponsor",
  b_rated: "B-rated sponsor",
  provisional: "Provisional sponsor",
};

const STRENGTH = {
  strong: "Sponsorship strength: strong",
  moderate: "Sponsorship strength: moderate",
  low: "Sponsorship strength: low",
};

// The same icons the profiled cards already use, so a row from the FOI file
// and a row from our own directory read as one list.
const Stat = ({ icon, children, wrap }) => (
  <li className={wrap ? "wrap" : undefined}>
    <img src={`${paths.SITE_BASE_URL}/images/icon_mini_${icon}.png`} alt="" />
    {children}
  </li>
);

export default function CompanyCard({ company, onUnsave }) {
  const name = company?.name || "";

  // Shortlisting. Optimistic: the button flips immediately and reverts if the
  // request fails, because waiting on a round trip to acknowledge a bookmark
  // makes it feel broken.
  const [saved, setSaved] = useState(!!company?.saved);
  const [busy, setBusy] = useState(false);

  const toggleSave = () => {
    if (busy) return;
    const next = !saved;
    setSaved(next);
    setBusy(true);
    http
      .post(
        "save-company",
        // The token is how MY_Controller resolves the member; without it the
        // endpoint has no mem_id and returns success having saved nothing.
        doObjToFormData({
          org_name: name,
          company_id: company?.id || "",
          token: getAuthUserToken(),
        }),
      )
      .then(({ data }) => {
        if (data?.status !== 1) setSaved(!next);
        else if (!next && onUnsave) onUnsave(name);
      })
      .catch(() => setSaved(!next))
      .finally(() => setBusy(false));
  };
  // encoded_id is only set for published directory entries; the rest of the
  // register contributes its facts without a page to link to.
  const detail = company?.encoded_id
    ? websiteLink(`/company-details/${company.encoded_id}/${toSlugUrl(name)}`)
    : null;

  const rating = RATINGS[company?.sponsor_rating];
  const strength = STRENGTH[String(company?.strength || "").toLowerCase()];

  // The heading above the list already names the role, so repeating it on
  // every card only made the line long enough to wrap - and lowercasing it
  // turned "IT business analysts" into "it business analysts".
  const cosLine = company?.cos_label
    ? `${company.cos_label} sponsored in 2025`
    : null;

  return (
    <div className="directory_card">
      <div className="head">
        {company?.image ? (
          <div className="logo">
            <ImageControl src={company.image} folder="companies" isThumb={true} />
          </div>
        ) : (
          <div className="monogram" aria-hidden="true">
            {initials(name)}
          </div>
        )}
        <div className="title">
          <div className="h5 m-0">
            {detail ? <Link to={detail}>{name}</Link> : <span>{name}</span>}
          </div>
        </div>
        <button
          type="button"
          className={saved ? "save_btn saved" : "save_btn"}
          onClick={toggleSave}
          aria-pressed={saved}
          aria-label={saved ? `Remove ${name} from your list` : `Save ${name}`}
          title={saved ? "Saved" : "Save this company"}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"
              fill={saved ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>

      </div>

      <ul className="mini_list">
        {cosLine && <Stat icon="file">{cosLine}</Stat>}
        {rating && <Stat icon="shield">{rating}</Stat>}
        {strength && <Stat icon="chart">{strength}</Stat>}
        {company?.overseas === "yes" && (
          <Stat icon="check">Accepts applicants from overseas</Stat>
        )}
        {company?.visa_support && (
          <Stat icon="visa" wrap>
            {company.visa_support}
          </Stat>
        )}
      </ul>

      {/* Footer pinned to the bottom of the card so the CTAs line up across a
          row whatever each card holds above them. Live jobs sit here rather
          than in the header: it is an action, and it belongs beside the other
          one. */}
      <div className="btm_blk">
        {detail ? <Link to={detail}>View company</Link> : <span />}
        {company?.job_count > 0 && (
          <Link
            className="jobs_badge"
            to={`/open-jobs/1?searchKeyword=${encodeURIComponent(name)}`}
          >
            {company.job_count} live {company.job_count === 1 ? "job" : "jobs"}
          </Link>
        )}
      </div>
    </div>
  );
}
